import Utils from './Utils';

export const PAGE_TITLE_POSTER_WIDTH = 192;
export const PAGE_TITLE_POSTER_HEIGHT = 288;

/** Near-square images stay "default"; clear landscape/portrait map to banner/poster. */
const ORIENTATION_RATIO_EPSILON = 0.05;

/**
 * Soft-deprecate `default` / missing type as the cover role `poster`.
 * @param {string|null|undefined} type
 * @returns {string}
 */
export function normalize_event_poster_role(type) {
    if (type === Utils.IMAGE_TYPE_BANNIERE)
        return Utils.IMAGE_TYPE_BANNIERE;
    if (type === Utils.IMAGE_TYPE_STILL)
        return Utils.IMAGE_TYPE_STILL;
    return Utils.IMAGE_TYPE_POSTER;
}

/**
 * @param {object|null|undefined} poster
 * @returns {string}
 */
export function poster_url(poster) {
    const url = poster?.url;
    if (typeof url !== 'string')
        return '';
    const trimmed = url.trim();
    if (!trimmed || trimmed === 'undefined' || trimmed === 'null')
        return '';
    return trimmed;
}

/**
 * CSS object-position / background-position from a normalized focal point.
 * @param {object|null|undefined} poster
 * @returns {string} e.g. "50% 25%"
 */
export function focal_point_css(poster) {
    const fp = poster?.focal_point;
    const x = Number(fp?.x);
    const y = Number(fp?.y);
    if (!Number.isFinite(x) || !Number.isFinite(y))
        return '50% 50%';
    const cx = Math.min(1, Math.max(0, x));
    const cy = Math.min(1, Math.max(0, y));
    return `${cx * 100}% ${cy * 100}%`;
}

/**
 * Pick the best poster for a semantic role (+ optional lang).
 * Order: role+lang → role+no lang → role+any lang → first usable (compat).
 *
 * @param {Array|null|undefined} posters
 * @param {{ role?: string, lang?: string|null }} [options]
 * @returns {object|null}
 */
export function pick_poster(posters, options = {}) {
    const list = (Array.isArray(posters) ? posters : []).filter((p) => poster_url(p));
    if (!list.length)
        return null;

    const wanted = normalize_event_poster_role(options.role);
    const by_role = list.filter(
        (p) => normalize_event_poster_role(p?.type) === wanted,
    );
    const pool = by_role.length ? by_role : list;
    const lang = options.lang || null;

    if (lang) {
        const with_lang = pool.filter((p) => p?.lang === lang);
        if (with_lang.length)
            return with_lang[0];
        const no_lang = pool.filter((p) => !p?.lang);
        if (no_lang.length)
            return no_lang[0];
    }

    return pool[0];
}

export function first_poster_url(posters, options) {
    const picked = pick_poster(posters, options || { role: Utils.IMAGE_TYPE_POSTER });
    return poster_url(picked);
}

export function first_poster_page_title_url(
    posters,
    width = PAGE_TITLE_POSTER_WIDTH,
    height = PAGE_TITLE_POSTER_HEIGHT,
    options,
) {
    const url = first_poster_url(posters, options);
    if (!url)
        return null;
    return Utils.imgProxyUrl(url, width, height) || null;
}

export function first_event_posters(events) {
    const list = Array.isArray(events) ? events : [];
    for (const event of list) {
        const posters = event?.posters;
        if (Array.isArray(posters) && first_poster_url(posters))
            return posters;
    }
    return list[0]?.posters;
}

/**
 * Infer event image type from pixel size (banner = landscape, poster = portrait).
 * Near-square / unknown → poster (default type is no longer offered in the UI).
 * @param {number} width
 * @param {number} height
 * @returns {string}
 */
export function infer_event_poster_type(width, height) {
    const w = Number(width);
    const h = Number(height);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0)
        return Utils.IMAGE_TYPE_POSTER;
    const ratio = w / h;
    if (ratio > 1 + ORIENTATION_RATIO_EPSILON)
        return Utils.IMAGE_TYPE_BANNIERE;
    return Utils.IMAGE_TYPE_POSTER;
}
